import { createContext, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getCustomerReport,
  createCustomerReport,
  closeCustomerReport,
  getCustomerReportAction,
} from 'actions/grace';
import { setItem, getItem, removeItem } from 'helpers/localStorage';
import { MODAL_TYPES, RESOLVED, ADA_RESOLUTION_STAGE_SLUG } from 'lib/constants';
import * as fromGrace from 'reducers/grace';
import * as ErrorLogging from 'services/ErrorLogging';

export const CustomerReportContext = createContext(null);

export const CustomerReportProvider = ({ children }) => {
  const [actionsTaken, setActionsTaken] = useState([]);
  const [endFindReport, setEndFindReport] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [meal, setMeal] = useState(null);
  const [ingredient, setIngredient] = useState(null);
  const [currentModal, setCurrentModal] = useState(MODAL_TYPES.help);
  const [closedReportId, setClosedReportId] = useState(null);

  const dispatch = useDispatch();

  const currentStage = useSelector(state => fromGrace.selectCurrentStage(state));
  const customerReport = useSelector(state => fromGrace.selectCustomerReport(state));
  const availableBaskets = useSelector(state => fromGrace.selectBasketsWithMeals(state));
  const isUserLoaded = useSelector(state => state.user.isLoaded);

  useEffect(() => {
    if (!isUserLoaded) return;

    function setFirstLoadDone() {
      setItem('firstLoadDone', true);
    }
    if (getItem('firstLoadDone') === null) {
      window.addEventListener('unload', setFirstLoadDone);
    } else if (!endFindReport) {
      findCurrentReport();
      setEndFindReport(true);
    }
    return () => {
      window.removeEventListener('unload', setFirstLoadDone);
    };
  }, [endFindReport, findCurrentReport, isUserLoaded]);

  const handleError = error => {
    setErrorMessage(error);
    ErrorLogging.captureException(error.message);
  };

  const storeReportMetadata = reportId => {
    const metadata = { reportId };
    setItem(`customerReport_${reportId}`, JSON.stringify(metadata));
  };

  const findCurrentReport = useCallback(async () => {
    const isReportFromThisDevice = reportId => {
      const storedData = getItem(`customerReport_${reportId}`);
      if (!storedData) return false;

      try {
        const metadata = JSON.parse(storedData);
        return metadata.reportId === reportId;
      } catch (e) {
        handleError(e);
        return false;
      }
    };
    setCurrentModal(MODAL_TYPES.grace);
    try {
      const result = await dispatch(getCustomerReport());
      if (result.customerReport) {
        const reportId = result.customerReport.id;

        if (!isReportFromThisDevice(reportId)) {
          setCurrentModal(MODAL_TYPES.help);
          return;
        }

        result.customerReport.actionsTaken && setActionsTaken(result.customerReport.actionsTaken);
        result.customerReport.basket && setSelectedBasket(result.customerReport.basket);
        result.customerReport.meal && setMeal(result.customerReport.meal);
      }
    } catch (e) {
      handleError(e);
    }
  }, [dispatch]);

  const startReport = useCallback(async () => {
    setErrorMessage(false);
    try {
      const result = await dispatch(createCustomerReport());
      if (!result.ok) {
        handleError(result.error);
        return;
      }
      const reportId = result.customerReport.id;

      storeReportMetadata(reportId);
      setCurrentModal(MODAL_TYPES.grace);
      setActionsTaken([
        {
          customerReportId: result.customerReport.id,
          reportStageOption: null,
          currentStage: result.customerReport.nextStage,
        },
      ]);
      setSelectedBasket(null);
      setMeal(null);
    } catch (e) {
      handleError(e);
    }
  }, [dispatch]);

  const closeReport = useCallback(async () => {
    let customerReportId = customerReport?.id;
    setClosedReportId(customerReport?.id);

    removeItem(`customerReport_${customerReportId}`);
    setActionsTaken([]);
    setSelectedBasket(null);
    setMeal(null);

    if (customerReportId) {
      try {
        const result = await dispatch(closeCustomerReport({ customerReportId }));
        if (
          result.reportStatus == RESOLVED &&
          !(currentStage?.slug == ADA_RESOLUTION_STAGE_SLUG && actionsTaken.length === 1)
        ) {
          setTimeout(() => {
            setCurrentModal(MODAL_TYPES.survey);
          }, 500);
        } else {
          setCurrentModal(MODAL_TYPES.help);
        }
      } catch (e) {
        handleError(e);
      }
    } else {
      setCurrentModal(MODAL_TYPES.help);
    }
  }, [actionsTaken.length, currentStage?.slug, customerReport?.id, dispatch]);

  const closeOutdatedModal = () => {
    removeItem(`customerReport_${customerReport.id}`);
    setCurrentModal(MODAL_TYPES.help);
    setClosedReportId(customerReport?.id);
    setActionsTaken([]);
    setSelectedBasket(null);
    setMeal(null);
  };

  const closeSurvey = useCallback(async () => {
    setTimeout(() => {
      setCurrentModal(MODAL_TYPES.help);
    }, 300);
  }, []);

  const goBack = async () => {
    //Get the previous stage/action (one before current)
    let targetAction = actionsTaken.at(-2);
    let incidentInfo = {};
    if (meal) incidentInfo = { mealSelectionId: meal.mealSelectionId };

    // Update the state with the new array minus the current action
    setActionsTaken(actionsTaken.slice(0, -1));
    // Post to the actions controller with the previous stage/action
    try {
      let result;
      if (targetAction.reportStageOption === null) {
        result = await dispatch(getCustomerReport(true));
      } else if (targetAction.reportStageOption !== null) {
        result = await dispatch(
          getCustomerReportAction({
            customerReportId: targetAction.customerReportId,
            reportStageOptionId: targetAction?.reportStageOption?.id,
            incidentInfo,
          })
        );
      }
      if (!result.ok) {
        handleError(result.error);
        return;
      } else {
        setErrorMessage(false);
      }
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <CustomerReportContext.Provider
      value={{
        customerReport,
        currentStage,
        actionsTaken,
        setActionsTaken,
        goBack,
        findCurrentReport,
        startReport,
        closeReport,
        errorMessage,
        setErrorMessage,
        availableBaskets,
        selectedBasket,
        setSelectedBasket,
        meal,
        setMeal,
        ingredient,
        setIngredient,
        currentModal,
        setCurrentModal,
        closedReportId,
        closeSurvey,
        closeOutdatedModal,
        isUserLoaded,
      }}
    >
      {children}
    </CustomerReportContext.Provider>
  );
};

CustomerReportProvider.propTypes = {
  children: PropTypes.node,
};
